import { useMemo } from 'react';
import { useAuth } from '../context/AuthContext';

/**
 * Custom hook to check user permissions
 * @returns {Object} Permission checking functions
 */
export const usePermissions = () => {
  const { user } = useAuth();

  return useMemo(() => {
    const role = user?.role || '';
    const isAdminUser = role === 'admin' || user?.permissions?.all === true;
    const isClinicAdminUser = role === 'clinic_admin';
    const isTherapistUser = role === 'therapist';

    const hasPermission = (module, action) => {
      if (!user) return false;

      if (isAdminUser) {
        return true;
      }

      const modulePerms = user.permissions?.[module];
      return modulePerms && modulePerms[action] === true;
    };

    const canRead = (module) => hasPermission(module, 'read');
    const canCreate = (module) => hasPermission(module, 'create');
    const canUpdate = (module) => hasPermission(module, 'update');
    const canDelete = (module) => hasPermission(module, 'delete');

    const hasModuleAccess = (module) => {
      if (!user) return false;
      if (isAdminUser) return true;
      return !!user.permissions?.[module];
    };

    const canViewPricing =
      isAdminUser ||
      isClinicAdminUser ||
      hasPermission('payments', 'read') ||
      hasPermission('invoices', 'read') ||
      hasPermission('finance', 'read');

    return {
      hasPermission,
      canRead,
      canCreate,
      canUpdate,
      canDelete,
      hasModuleAccess,
      isAdmin: isAdminUser,
      isClinicAdmin: isClinicAdminUser,
      isTherapist: isTherapistUser,
      isNonAdmin: !!user && !isAdminUser,
      canViewPricing,
    };
  }, [user]);
};
